import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Star, Upload, X, ChevronDown, ChevronUp, MessageSquare, RotateCcw, HelpCircle, Send, User, Download } from 'lucide-react';
import AddressManager from '../components/AddressManager';

const StarInput = ({ value, onChange, label }) => {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  const color = display >= 3.5 ? 'text-green-500' : display >= 2 ? 'text-orange-500' : 'text-red-500';
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-forest/60 mb-1">{label} *</p>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map(i => (
          <button key={i} type="button" onClick={() => onChange(i)}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
            className="p-0.5">
            <Star className={`w-6 h-6 transition-colors ${i <= display ? `${color} fill-current` : 'text-gray-300'}`} />
          </button>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, loading: authLoading, logout, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [reviews, setReviews] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', date_of_birth: '', gender: '', alternate_phone: '', about: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  // Review modal state
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewForm, setReviewForm] = useState({ product_rating: 0, delivery_rating: 0, product_review: '', delivery_review: '', media_urls: [] });
  const [uploading, setUploading] = useState(false);

  // Return/Exchange modal state
  const [returnModal, setReturnModal] = useState(null);
  const [returnForm, setReturnForm] = useState({ type: 'return', reason: '', media_urls: [] });
  const [returnUploading, setReturnUploading] = useState(false);

  // Help modal state
  const [helpModal, setHelpModal] = useState(null);
  const [helpForm, setHelpForm] = useState({ issue_tag: '', title: '', description: '' });
  const [issueTags, setIssueTags] = useState([]);
  // Ticket chat
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');

  useEffect(() => {
    if (authLoading) return; // Wait for auth to resolve
    if (!user) { navigate('/login'); return; }
    fetchOrders();
    fetchReviews();
    fetchTickets();
    fetchIssueTags();
    fetchProfile();
  }, [user, authLoading, navigate]);

  const fetchOrders = async () => { try { const r = await api.get('/orders'); setOrders(r.data || []); } catch {} };
  const fetchReviews = async () => { try { const r = await api.get('/reviews/user'); setReviews(r.data || []); } catch {} };
  const fetchTickets = async () => { try { const r = await api.get('/support/tickets'); setTickets(r.data || []); } catch {} };
  const fetchIssueTags = async () => { try { const r = await api.get('/support/tags'); setIssueTags(r.data?.tags || []); } catch {} };
  const fetchProfile = async () => {
    try {
      const r = await api.get('/profile');
      setProfile(r.data);
      setProfileForm({
        name: r.data.name || '', phone: r.data.phone || '',
        date_of_birth: r.data.date_of_birth || '', gender: r.data.gender || '',
        alternate_phone: r.data.alternate_phone || '', about: r.data.about || ''
      });
    } catch {}
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      await api.put('/profile', profileForm);
      toast.success('Profile updated');
      fetchProfile();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    setProfileSaving(false);
  };

  const handleProfileClear = async () => {
    if (!window.confirm('Clear all profile information (name, phone will be kept)?')) return;
    try {
      await api.delete('/profile');
      toast.success('Profile info cleared');
      fetchProfile();
    } catch (error) { toast.error('Failed'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(newPassword)) { toast.error('Must include uppercase letter'); return; }
    if (!/[a-z]/.test(newPassword)) { toast.error('Must include lowercase letter'); return; }
    if (!/[0-9]/.test(newPassword)) { toast.error('Must include a number'); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)) { toast.error('Must include special character'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    try {
      const r = await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      loginWithToken(r.data.token, r.data.user);
      toast.success('Password changed. All other sessions logged out.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Cancel this order?')) return;
    try { await api.post(`/orders/${orderId}/cancel`); toast.success('Order cancelled'); fetchOrders(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Cannot cancel'); }
  };

  // Review
  const openReviewModal = (order, item) => {
    setReviewModal({ order, item });
    setReviewForm({ product_rating: 0, delivery_rating: 0, product_review: '', delivery_review: '', media_urls: [] });
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 20MB limit`);
        continue;
      }
      const allowed = ['image/jpeg','image/png','image/webp','video/mp4','video/quicktime'];
      if (!allowed.includes(file.type)) {
        toast.error(`${file.name}: Only JPG, PNG, WebP images and MP4/MOV videos allowed`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post('/reviews/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setReviewForm(prev => ({ ...prev, media_urls: [...prev.media_urls, r.data.url] }));
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || 'Upload failed'}`);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const submitReview = async () => {
    if (!reviewForm.product_rating || !reviewForm.delivery_rating) { toast.error('Star ratings are required'); return; }
    try {
      await api.post('/reviews', {
        order_id: reviewModal.order.order_id,
        product_id: reviewModal.item.product_id,
        ...reviewForm
      });
      toast.success('Review submitted!');
      setReviewModal(null);
      fetchReviews();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // Return / Exchange
  const canReturn = (order) => {
    if (!['delivered', 'exchange_delivered'].includes(order.order_status)) return false;
    const delivered = order.delivered_at || order.updated_at || order.created_at;
    const diff = (Date.now() - new Date(delivered).getTime()) / (1000 * 60 * 60 * 24);
    return diff <= 7;
  };

  const submitReturnExchange = async () => {
    if (!returnForm.reason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      await api.post(`/orders/${returnModal.order_id}/return-exchange`, {
        order_id: returnModal.order_id,
        request_type: returnForm.type,
        reason: returnForm.reason,
        media_urls: returnForm.media_urls
      });
      toast.success(`${returnForm.type === 'return' ? 'Return' : 'Exchange'} request submitted`);
      setReturnModal(null);
      setReturnForm({ type: 'return', reason: '', media_urls: [] });
      fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // Help Ticket
  const submitHelpTicket = async () => {
    if (!helpForm.title.trim()) { toast.error('Title is required'); return; }
    if (!helpForm.issue_tag) { toast.error('Please select an issue type'); return; }
    try {
      await api.post('/support/tickets', {
        order_id: helpModal?.order_id || null,
        product_id: helpModal?.product_id || null,
        ...helpForm
      });
      toast.success('Support ticket created');
      setHelpModal(null);
      setHelpForm({ issue_tag: '', title: '', description: '' });
      fetchTickets();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const hasReviewed = (orderId, productId) => reviews.some(r => r.order_id === orderId && r.product_id === productId);
  const isReturnExchange = (status) => ['return_requested', 'return_approved', 'return_picked', 'returned', 'exchange_requested', 'exchange_approved', 'exchange_shipped', 'exchange_delivered'].includes(status);

  const statusColor = (s) => {
    if (s === 'delivered' || s === 'exchange_delivered' || s === 'returned') return 'text-green-700 bg-green-50';
    if (s === 'cancelled') return 'text-red-700 bg-red-50';
    if (isReturnExchange(s)) return 'text-orange-700 bg-orange-50';
    return 'text-forest bg-gold/10';
  };

  const tabClass = (t) => `px-4 py-2.5 text-sm uppercase tracking-[0.1em] transition-all border-b-2 ${activeTab === t ? 'text-gold border-gold font-medium' : 'text-forest/50 border-transparent hover:text-forest'}`;

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}/invoice-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Vastram_Invoice_${orderId.slice(0, 8).toUpperCase()}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  // Send ticket reply (user)
  const handleSendTicketReply = async (ticketId) => {
    if (!ticketReply.trim()) return;
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, { text: ticketReply });
      setTicketReply('');
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };

  // Media upload for return/exchange
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const handleReturnMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setReturnUploading(true);
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 20MB limit`);
        continue;
      }
      const allowed = ['image/jpeg','image/png','image/webp','video/mp4','video/quicktime'];
      if (!allowed.includes(file.type)) {
        toast.error(`${file.name}: Only JPG, PNG, WebP images and MP4/MOV videos are allowed`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/reviews/upload-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        setReturnForm(f => ({ ...f, media_urls: [...f.media_urls, res.data.url] }));
      } catch (err) {
        const detail = err.response?.data?.detail || 'Upload failed. Please try again.';
        toast.error(`${file.name}: ${detail}`);
      }
    }
    setReturnUploading(false);
    e.target.value = '';
  };

  if (authLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">Loading...</p>
    </div>
  );
  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-12" data-testid="dashboard-page">
      <h1 className="font-heading text-3xl text-forest mb-2">My Account</h1>
      <p className="text-muted-foreground text-sm mb-8">Welcome back, {user.name || user.email}</p>

      <div className="flex space-x-2 border-b border-forest/10 mb-8 overflow-x-auto">
        <button onClick={() => setActiveTab('orders')} className={tabClass('orders')} data-testid="tab-orders">Orders</button>
        <button onClick={() => setActiveTab('profile')} className={tabClass('profile')} data-testid="tab-profile">Profile</button>
        <button onClick={() => setActiveTab('addresses')} className={tabClass('addresses')} data-testid="tab-addresses">Addresses</button>
        <button onClick={() => setActiveTab('support')} className={tabClass('support')} data-testid="tab-support">Help</button>
        <button onClick={() => setActiveTab('password')} className={tabClass('password')} data-testid="tab-password">Password</button>
      </div>

      {/* ——— PROFILE TAB ——— */}
      {activeTab === 'profile' && (
        <div className="max-w-lg space-y-6" data-testid="profile-tab">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-forest">My Profile</h2>
            <button onClick={handleProfileClear} className="text-xs text-red-500 hover:text-red-700 transition-colors" data-testid="clear-profile-button">
              Clear Extra Info
            </button>
          </div>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-forest/60 mb-1 block">Name</label>
              <input type="text" value={profileForm.name} onChange={(e) => setProfileForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="profile-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-forest/60 mb-1 block">Email</label>
              <input type="email" value={profile?.email || ''} disabled
                className="w-full px-4 py-3 bg-neutral-warm border border-forest/10 text-sm text-forest/60 cursor-not-allowed" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-forest/60 mb-1 block">Phone</label>
                <input type="tel" value={profileForm.phone} onChange={(e) => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="profile-phone" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-forest/60 mb-1 block">Alternate Phone</label>
                <input type="tel" value={profileForm.alternate_phone} onChange={(e) => setProfileForm(f => ({ ...f, alternate_phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="profile-alt-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-forest/60 mb-1 block">Date of Birth</label>
                <input type="date" value={profileForm.date_of_birth} onChange={(e) => setProfileForm(f => ({ ...f, date_of_birth: e.target.value }))}
                  className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="profile-dob" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-forest/60 mb-1 block">Gender</label>
                <select value={profileForm.gender} onChange={(e) => setProfileForm(f => ({ ...f, gender: e.target.value }))}
                  className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="profile-gender">
                  <option value="">Select</option>
                  <option value="Female">Female</option>
                  <option value="Male">Male</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-forest/60 mb-1 block">About</label>
              <textarea value={profileForm.about} onChange={(e) => setProfileForm(f => ({ ...f, about: e.target.value }))}
                placeholder="Tell us a bit about yourself..." rows={3}
                className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold resize-none" data-testid="profile-about" />
            </div>
            <button type="submit" disabled={profileSaving}
              className="bg-forest text-ivory px-8 py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors disabled:opacity-50"
              data-testid="save-profile-button">{profileSaving ? 'Saving...' : 'Save Profile'}</button>
          </form>
        </div>
      )}

      {/* ——— ORDERS TAB ——— */}
      {activeTab === 'orders' && (
        <div className="space-y-4" data-testid="orders-tab">
          {orders.length === 0 ? <p className="text-muted-foreground text-center py-12">No orders yet.</p> : orders.map(order => (
            <div key={order.order_id} className="bg-neutral-warm p-5 rounded-sm" data-testid="order-card">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-forest font-medium">Order #{order.order_id?.slice(0, 8)}</p>
                  <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</p>
                  {order.gift_wrap && <span className="text-xs bg-gold/20 text-forest px-2 py-0.5 mt-1 inline-block">Gift Wrapped</span>}
                </div>
                <div className="text-right">
                  <p className="font-heading text-lg text-forest">₹{order.total_amount?.toLocaleString()}</p>
                  <span className={`text-xs px-2 py-1 rounded-sm capitalize ${statusColor(order.order_status)}`}>
                    {order.order_status?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              {/* Order items */}
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex items-center space-x-4 py-2 border-t border-gold/10">
                  {item.image && <img src={item.image} alt={item.name} className="w-14 h-14 object-cover rounded-sm" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-forest truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">Qty: {item.quantity} {item.size ? `| Size: ${item.size}` : ''}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Review button */}
                    {(order.order_status === 'delivered' || order.order_status === 'exchange_delivered') && (
                      hasReviewed(order.order_id, item.product_id) ? (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-sm">Reviewed</span>
                      ) : (
                        <button onClick={() => openReviewModal(order, item)}
                          className="text-xs text-gold hover:text-forest transition-colors flex items-center space-x-1"
                          data-testid={`review-btn-${item.product_id}`}>
                          <Star className="w-3 h-3" /><span>Rate</span>
                        </button>
                      )
                    )}
                    {/* Help button */}
                    <button onClick={() => { setHelpModal({ order_id: order.order_id, product_id: item.product_id }); setHelpForm({ issue_tag: '', title: '', description: '' }); }}
                      className="text-xs text-forest/50 hover:text-forest transition-colors flex items-center space-x-1"
                      data-testid={`help-btn-${item.product_id}`}>
                      <HelpCircle className="w-3 h-3" /><span>Help</span>
                    </button>
                  </div>
                </div>
              ))}

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gold/10">
                {/* Cancel */}
                {['confirmed', 'processing'].includes(order.order_status) && (() => {
                  const mins = (Date.now() - new Date(order.created_at).getTime()) / 60000;
                  return mins <= 60;
                })() && (
                  <button onClick={() => handleCancelOrder(order.order_id)}
                    className="text-xs border border-red-300 text-red-600 px-3 py-1.5 hover:bg-red-50 transition-colors"
                    data-testid={`cancel-btn-${order.order_id}`}>Cancel Order</button>
                )}

                {/* Return / Exchange */}
                {['delivered', 'exchange_delivered'].includes(order.order_status) && (
                  canReturn(order) ? (
                    <button onClick={() => { setReturnModal(order); setReturnForm({ type: 'return', reason: '', media_urls: [] }); }}
                      className="text-xs border border-orange-300 text-orange-600 px-3 py-1.5 hover:bg-orange-50 transition-colors flex items-center space-x-1"
                      data-testid={`return-btn-${order.order_id}`}>
                      <RotateCcw className="w-3 h-3" /><span>Return / Exchange</span>
                    </button>
                  ) : (
                    <div className="relative group">
                      <button disabled className="text-xs border border-gray-200 text-gray-400 px-3 py-1.5 cursor-not-allowed flex items-center space-x-1"
                        data-testid={`return-disabled-${order.order_id}`}>
                        <RotateCcw className="w-3 h-3" /><span>Return / Exchange</span>
                      </button>
                      <div className="absolute bottom-full left-0 mb-1 w-72 bg-forest text-ivory text-xs p-3 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Product can't be returned or exchanged after 7 days. Please contact us on WhatsApp, phone, or email for more query.
                      </div>
                    </div>
                  )
                )}

                {/* Show return/exchange status */}
                {isReturnExchange(order.order_status) && (
                  <span className="text-xs text-orange-600 bg-orange-50 px-3 py-1.5 rounded-sm capitalize">
                    {order.order_status.replace(/_/g, ' ')} — {order.return_exchange_reason || 'Processing'}
                  </span>
                )}

                {/* Download Invoice */}
                {!['cancelled'].includes(order.order_status) && (
                  <button onClick={() => handleDownloadInvoice(order.order_id)}
                    className="text-xs border border-forest/30 text-forest px-3 py-1.5 hover:bg-forest/5 transition-colors flex items-center space-x-1"
                    data-testid={`invoice-btn-${order.order_id}`}>
                    <Download className="w-3 h-3" /><span>Invoice</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ——— ADDRESSES TAB ——— */}
      {activeTab === 'addresses' && <AddressManager />}

      {/* ——— SUPPORT TAB ——— */}
      {activeTab === 'support' && (
        <div className="space-y-4" data-testid="support-tab">
          <div className="flex justify-between items-center">
            <h2 className="font-heading text-xl text-forest">My Support Tickets</h2>
            <button onClick={() => { setHelpModal({}); setHelpForm({ issue_tag: '', title: '', description: '' }); }}
              className="bg-forest text-ivory px-4 py-2 text-sm uppercase tracking-[0.1em] hover:bg-forest/90 transition-colors"
              data-testid="new-ticket-button">New Ticket</button>
          </div>
          {tickets.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center">No support tickets.</p> : tickets.map(t => (
            <div key={t.ticket_id} className="bg-neutral-warm rounded-sm overflow-hidden" data-testid="ticket-card">
              <div className="p-5">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs bg-forest/10 text-forest px-2 py-0.5 rounded-sm">{t.issue_tag}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${t.status === 'resolved' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                        {t.status === 'resolved' ? 'Resolved' : 'Open'}
                      </span>
                    </div>
                    <h3 className="text-forest font-medium text-sm">{t.title}</h3>
                    {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <button onClick={() => setExpandedTicket(expandedTicket === t.ticket_id ? null : t.ticket_id)}
                      className="text-xs border border-forest/20 text-forest px-3 py-1.5 rounded-sm hover:bg-forest/5 flex items-center gap-1"
                      data-testid="ticket-chat-toggle">
                      <MessageSquare className="w-3 h-3" /> {(t.messages?.length || 0) > 0 ? `${t.messages.length} replies` : 'Chat'}
                    </button>
                    <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
              {/* Conversation */}
              {expandedTicket === t.ticket_id && (
                <div className="border-t border-gold/10 p-5 bg-ivory/50" data-testid="ticket-conversation">
                  <div className="space-y-3 max-h-60 overflow-y-auto mb-3">
                    {(t.messages || []).map(msg => (
                      <div key={msg.message_id} className={`flex items-start gap-2 ${msg.sender === 'admin' ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${msg.sender === 'admin' ? 'bg-gold/20' : 'bg-forest/10'}`}>
                          {msg.sender === 'admin' ? <Star className="w-3 h-3 text-gold" /> : <User className="w-3 h-3 text-forest" />}
                        </div>
                        <div className={`flex-1 p-3 rounded-sm border ${msg.sender === 'admin' ? 'bg-gold/5 border-gold/20' : 'bg-white border-forest/10'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-forest">{msg.sender === 'admin' ? 'Vastram Support' : 'You'}</span>
                            <span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-forest">{msg.text}</p>
                        </div>
                      </div>
                    ))}
                    {(!t.messages || t.messages.length === 0) && (
                      <p className="text-xs text-muted-foreground text-center py-2">No replies yet. Our team will respond within 3 business days.</p>
                    )}
                  </div>
                  {/* User Reply */}
                  <div className="flex gap-2" data-testid="ticket-user-reply">
                    <input type="text" value={ticketReply} onChange={(e) => setTicketReply(e.target.value)}
                      placeholder="Type a reply..."
                      onKeyDown={(e) => e.key === 'Enter' && handleSendTicketReply(t.ticket_id)}
                      className="flex-1 px-3 py-2 bg-white border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold rounded-sm"
                      data-testid="ticket-reply-input" />
                    <button onClick={() => handleSendTicketReply(t.ticket_id)}
                      className="bg-forest text-ivory px-4 py-2 rounded-sm hover:bg-forest/90 transition-colors"
                      data-testid="ticket-send-reply">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ——— PASSWORD TAB ——— */}
      {activeTab === 'password' && (
        <form onSubmit={handleChangePassword} className="max-w-md space-y-4" data-testid="change-password-form">
          <input type="password" placeholder="Current Password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="current-password-input" />
          <input type="password" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="new-password-input" />
          <input type="password" placeholder="Confirm New Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="confirm-password-input" />
          <button type="submit" disabled={!currentPassword || (newPassword !== confirmPassword) || !newPassword}
            className="bg-forest text-ivory px-8 py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors disabled:opacity-50"
            data-testid="change-password-button">Change Password</button>
        </form>
      )}

      {/* ——— REVIEW MODAL ——— */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="bg-ivory w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-xl text-forest">Rate & Review</h3>
              <button onClick={() => setReviewModal(null)} className="text-forest/40 hover:text-forest"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground">{reviewModal.item.name}</p>
            <StarInput label="Product Rating" value={reviewForm.product_rating} onChange={(v) => setReviewForm(f => ({ ...f, product_rating: v }))} />
            <textarea value={reviewForm.product_review} onChange={(e) => setReviewForm(f => ({ ...f, product_review: e.target.value }))}
              placeholder="Product review (optional)" rows={2}
              className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold resize-none" />
            <StarInput label="Delivery Rating" value={reviewForm.delivery_rating} onChange={(v) => setReviewForm(f => ({ ...f, delivery_rating: v }))} />
            <textarea value={reviewForm.delivery_review} onChange={(e) => setReviewForm(f => ({ ...f, delivery_review: e.target.value }))}
              placeholder="Delivery review (optional)" rows={2}
              className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold resize-none" />
            <div>
              <p className="text-xs uppercase tracking-widest text-forest/60 mb-2">Photos / Videos</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {reviewForm.media_urls.map((url, i) => (
                  <div key={i} className="relative w-16 h-16">
                    <img src={url} alt="" className="w-full h-full object-cover rounded-sm" />
                    <button onClick={() => setReviewForm(f => ({ ...f, media_urls: f.media_urls.filter((_, j) => j !== i) }))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">x</button>
                  </div>
                ))}
              </div>
              <label className="flex items-center space-x-2 text-sm text-forest cursor-pointer hover:text-gold transition-colors">
                <Upload className="w-4 h-4" />
                <span>{uploading ? 'Uploading...' : 'Add Photos/Videos'}</span>
                <input type="file" multiple accept="image/*,video/*" onChange={handleMediaUpload} className="hidden" data-testid="review-media-upload" />
              </label>
            </div>
            <button onClick={submitReview} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors"
              data-testid="submit-review-button">Submit Review</button>
          </div>
        </div>
      )}

      {/* ——— RETURN/EXCHANGE MODAL ——— */}
      {returnModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="return-modal">
          <div className="bg-ivory w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-xl text-forest">Return / Exchange</h3>
              <button onClick={() => { setReturnModal(null); setReturnForm({ type: 'return', reason: '', media_urls: [] }); }} className="text-forest/40 hover:text-forest"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground">Order #{returnModal.order_id?.slice(0, 8)}</p>
            <div className="flex gap-3">
              <button onClick={() => setReturnForm(f => ({ ...f, type: 'return' }))}
                className={`flex-1 py-2.5 text-sm border-2 transition-all ${returnForm.type === 'return' ? 'border-forest bg-forest text-ivory' : 'border-forest/15 text-forest'}`}>
                Return
              </button>
              <button onClick={() => setReturnForm(f => ({ ...f, type: 'exchange' }))}
                className={`flex-1 py-2.5 text-sm border-2 transition-all ${returnForm.type === 'exchange' ? 'border-forest bg-forest text-ivory' : 'border-forest/15 text-forest'}`}>
                Exchange
              </button>
            </div>
            <textarea value={returnForm.reason} onChange={(e) => setReturnForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Reason for return/exchange *" rows={3}
              className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold resize-none" data-testid="return-reason-input" />
            {/* Media Upload for Return/Exchange */}
            <div>
              <p className="text-xs uppercase tracking-widest text-forest/60 mb-2">Photos / Videos of Issue</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {returnForm.media_urls.map((url, i) => (
                  <div key={i} className="relative w-16 h-16">
                    {url.includes('.mp4') || url.includes('.mov') ? (
                      <video src={url} className="w-full h-full object-cover rounded-sm" />
                    ) : (
                      <img src={url} alt="" className="w-full h-full object-cover rounded-sm" />
                    )}
                    <button onClick={() => setReturnForm(f => ({ ...f, media_urls: f.media_urls.filter((_, j) => j !== i) }))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">x</button>
                  </div>
                ))}
              </div>
              <label className="flex items-center space-x-2 text-sm text-forest cursor-pointer hover:text-gold transition-colors">
                <Upload className="w-4 h-4" />
                <span>{returnUploading ? 'Uploading...' : 'Add Photos/Videos'}</span>
                <input type="file" multiple accept="image/*,video/*" onChange={handleReturnMediaUpload} className="hidden" data-testid="return-media-upload" />
              </label>
              <p className="text-xs text-muted-foreground mt-1">Upload photos/videos showing the defect or issue</p>
            </div>
            <button onClick={submitReturnExchange} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors"
              data-testid="submit-return-button">{returnForm.type === 'return' ? 'Submit Return Request' : 'Submit Exchange Request'}</button>
          </div>
        </div>
      )}

      {/* ——— HELP MODAL ——— */}
      {helpModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="help-modal">
          <div className="bg-ivory w-full max-w-md p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-xl text-forest">Report an Issue</h3>
              <button onClick={() => setHelpModal(null)} className="text-forest/40 hover:text-forest"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-forest/60 mb-2">Select Issue Type *</p>
              <div className="flex flex-wrap gap-2">
                {issueTags.map(tag => (
                  <button key={tag} onClick={() => setHelpForm(f => ({ ...f, issue_tag: tag, title: f.title || tag }))}
                    className={`text-xs px-3 py-1.5 border transition-all ${helpForm.issue_tag === tag ? 'border-forest bg-forest text-ivory' : 'border-forest/15 text-forest/60 hover:text-forest'}`}
                    data-testid={`issue-tag-${tag.replace(/\s+/g, '-')}`}>{tag}</button>
                ))}
                <button onClick={() => setHelpForm(f => ({ ...f, issue_tag: 'Other' }))}
                  className={`text-xs px-3 py-1.5 border transition-all ${helpForm.issue_tag === 'Other' ? 'border-forest bg-forest text-ivory' : 'border-forest/15 text-forest/60 hover:text-forest'}`}>
                  Other
                </button>
              </div>
            </div>
            <input type="text" value={helpForm.title} onChange={(e) => setHelpForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Issue Title *"
              className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="help-title-input" />
            <textarea value={helpForm.description} onChange={(e) => setHelpForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe your issue in detail (optional)" rows={3}
              className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm focus:outline-none focus:border-gold resize-none" data-testid="help-description-input" />
            <button onClick={submitHelpTicket} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors"
              data-testid="submit-help-button">Submit</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
